// ── Изучаемый язык: выбор, свой язык, первый вопрос, мастер «проекты без языка» ──

import { useEffect, useMemo, useRef, useState } from "react";
import { t } from "../i18n";
import Icon from "../Icon";
import { SANS } from "../themes";
import { CUSTOM_LANGS_CHANGE_EVENT, addCustomLang, getCustomLangs, removeCustomLang } from "../utils";
import { Button, Confirm, Field, IconButton, Modal, Sheet } from "../ui";
import { usePalette } from "../ui/theme";
import { panelSurface } from '../ui/surface';
import { floatSurface } from '../ui/surface';

// ── Баннер миграции: проекты без языка → быстрый мастер ──────────────────────
export function LangMigrationBanner({projects=[],textProjects=[],onAssignLang,activeLang}){
  const C=usePalette();
  const [open,setOpen]=useState(false);
  const unassignedAudio=projects.filter(p=>!p.src_lang);
  const unassignedText =textProjects.filter(p=>!p.src_lang||p.src_lang==='en'); // 'en' дефолт от старого createTextProject — считаем не помеченным
  const totalUnassigned=unassignedAudio.length+unassignedText.length;
  // Группировка по папкам для мастера
  const grouped=useMemo(()=>{
    if(!open)return null;
    const g={};
    [...unassignedAudio.map(p=>({...p,_kind:'audio'})),...unassignedText.map(p=>({...p,_kind:'text'}))].forEach(p=>{
      const k=p.folder||'__none__';
      (g[k]=g[k]||[]).push(p);
    });
    return g;
  },[open,unassignedAudio,unassignedText]);
  if(totalUnassigned===0)return null;
  return(
    <>
      <div style={{background:C.warmPale,border:`1px solid ${C.warm}`,borderRadius:12,padding:'10px 14px',
                   marginBottom:14,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <span style={{display:'inline-flex',color:C.warmDark}}><Icon n="globe" s={20}/></span>
        <div style={{flex:'1 1 160px',minWidth:0}}>
          <div style={{fontSize:13,fontWeight:700,color:C.warmDark}}>
            {totalUnassigned} {totalUnassigned===1?t("проект"):totalUnassigned<5?t("проекта"):t("проектов")} {t("без языка")}
          </div>
          <div style={{fontSize:12,color:C.mid}}>
            {t("Их прослушивание/чтение не попадает в статистику по языкам.")}
          </div>
        </div>
        <Button size="sm" variant="primary" onClick={()=>setOpen(true)}>{t("Назначить")}</Button>
      </div>
      {open&&(
        <Sheet size="lg" title={t("Назначить язык проектам")} onClose={()=>setOpen(false)}
          footer={<Button variant="primary" onClick={()=>setOpen(false)}>{t("Готово")}</Button>}>
          <div style={{fontSize:13,color:C.faint,marginBottom:14,lineHeight:1.5}}>
            {t("Выберите язык для каждой папки — он применится ко всем проектам внутри. Для папок, которые пока не хотите размечать, выберите «Пропустить».")}
          </div>
          {Object.entries(grouped||{}).map(([folder,items])=>(
            <FolderLangRow key={folder} folder={folder} items={items}
              defaultLang={activeLang||''}
              onApply={(lang)=>{
                if(!lang)return;
                // handleAssignLang — сначала на устройстве, потом в фоне на сервер:
                // пакет применяется мгновенно даже для больших папок.
                items.forEach(p=>onAssignLang?.(p.id,lang,p._kind==='text'));
              }}/>
          ))}
        </Sheet>
      )}
    </>
  );
}

export function FolderLangRow({folder,items,defaultLang,onApply}){
  const C=usePalette();
  const [lang,setLang]=useState(defaultLang||'');
  const [applied,setApplied]=useState(false);
  const folderLabel=folder==='__none__'?t("(Без папки)"):folder;
  const allOpts=useAllLangOpts();
  return(
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,
                 padding:'10px 12px',marginBottom:8}}>
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <span style={{fontSize:16}}><Icon n="folder"/></span>
        <span style={{fontSize:13,fontWeight:700,color:C.text,flex:1,minWidth:120}}>
          {folderLabel} <span style={{color:C.faint,fontWeight:500}}>· {items.length}</span>
        </span>
        <select value={lang} onChange={e=>{setLang(e.target.value);setApplied(false);}} aria-label={t("Язык папки «{0}»",[folderLabel])}
          data-panel="" style={{...panelSurface(C),borderRadius:10,minHeight:36,maxWidth:'100%',
                  color:C.text,padding:'0 8px',fontSize:13,fontFamily:SANS,flexShrink:1,minWidth:0}}>
          <option value="">{t("— пропустить —")}</option>
          {allOpts.map(([code,flag,name])=>(
            <option key={code} value={code}>{flag} {name}</option>
          ))}
        </select>
        <Button size="sm" variant={applied?'secondary':'primary'} disabled={!lang||applied} onClick={()=>{onApply(lang);setApplied(true);}} style={{minHeight:36}}>
          {applied?'✓':t("Применить")}
        </Button>
      </div>
      <div style={{fontSize:12,color:C.faint,marginTop:6,paddingLeft:26,overflowWrap:'anywhere'}}>
        {items.slice(0,4).map(p=>p.name).join(' · ')}{items.length>4?` … +${items.length-4}`:''}
      </div>
    </div>
  );
}

// ── Приветственная модалка: спрашивает активный язык при первом запуске ──────
export function LangIntroModal({onPick,onSkip,theme}){
  const C=usePalette();
  const [showCustom,setShowCustom]=useState(false);
  const allOpts=useAllLangOpts();
  return(
    <Modal size="sm" dismissable={false} onClose={onSkip} closeLabel={t("Пропустить — выберу позже")}
      title={<span style={{display:'inline-flex',alignItems:'center',gap:8}}><Icon n="globe" s={20}/>{t("Какой язык вы изучаете?")}</span>}>
      <div style={{fontSize:13,color:C.faint,marginBottom:16,lineHeight:1.5}}>
        {t("AntonCards ведёт статистику отдельно для каждого языка.")}<br/>
        {t("Можно поменять в любой момент.")}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))',gap:8,marginBottom:10}}>
        {allOpts.map(([code,flag,name])=>(
          <button data-btn="" key={code} onClick={()=>onPick(code)} className="ac-ibtn"
            style={{display:'flex',alignItems:'center',gap:8,minHeight:44,background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:12,
                    padding:'8px 12px',fontSize:13.5,fontFamily:SANS,color:C.text,fontWeight:500,cursor:'pointer',textAlign:'left',minWidth:0}}>
            <span style={{fontSize:18,flexShrink:0}}>{flag}</span>
            <span style={{minWidth:0,overflowWrap:'anywhere'}}>{name}</span>
          </button>
        ))}
      </div>
      <Button full variant="ghost" onClick={()=>setShowCustom(true)} style={{border:`1px dashed ${C.border}`,marginBottom:6}}>
        {t("＋ Своего нет в списке — добавить")}
      </Button>
      <Button full variant="ghost" onClick={onSkip} style={{color:C.faint}}>{t("Пропустить — выберу позже")}</Button>
      {showCustom&&<CustomLangModal theme={theme} onClose={()=>setShowCustom(false)}
        onAdded={(code)=>{setShowCustom(false);onPick(code);}}/>}
    </Modal>
  );
}

// ── Активный язык (LingQ-style): chip-кнопка с выпадающим меню ────────────────
// Встроенный список. Пользовательские языки добавляются через CustomLangModal
// и хранятся в localStorage (см. getCustomLangs/saveCustomLangs в utils.js).
// Полный список (встроенные + пользовательские) получают через useAllLangOpts().
export const BUILTIN_LANG_CODES = new Set();
export const LANG_PICKER_OPTS_BUILTIN = [
  ['en','🇬🇧','English'],['ru','🇷🇺',t("Русский")],['de','🇩🇪','Deutsch'],
  ['fr','🇫🇷','Français'],['es','🇪🇸','Español'],['it','🇮🇹','Italiano'],
  ['pt','🇧🇷','Português'],['nl','🇳🇱','Nederlands'],['sv','🇸🇪','Svenska'],
  ['no','🇳🇴','Norsk'],['da','🇩🇰','Dansk'],['fi','🇫🇮','Suomi'],
  ['pl','🇵🇱','Polski'],['cs','🇨🇿','Čeština'],['uk','🇺🇦',t("Українська")],
  ['ro','🇷🇴','Română'],['hu','🇭🇺','Magyar'],['el','🇬🇷','Ελληνικά'],
  ['tr','🇹🇷','Türkçe'],['ar','🇸🇦','العربية'],['he','🇮🇱','עברית'],
  ['fa','🇮🇷','فارسی'],['hi','🇮🇳','हिन्दी'],['bn','🇧🇩','বাংলা'],
  ['th','🇹🇭','ไทย'],['vi','🇻🇳','Tiếng Việt'],['id','🇮🇩','Indonesia'],
  ['zh','🇨🇳','中文'],['ja','🇯🇵','日本語'],['ko','🇰🇷','한국어'],
];
// Backwards-compatible alias used in older code paths.
export const LANG_PICKER_OPTS = LANG_PICKER_OPTS_BUILTIN;
LANG_PICKER_OPTS_BUILTIN.forEach(([c])=>BUILTIN_LANG_CODES.add(c));

// React hook: full language list (built-in + user-defined), reactive to changes.
// Re-renders subscribers whenever a custom language is added/removed.
export function useAllLangOpts() {
  const [custom, setCustom] = useState(getCustomLangs);
  useEffect(() => {
    const refresh = () => setCustom(getCustomLangs());
    window.addEventListener(CUSTOM_LANGS_CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CUSTOM_LANGS_CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return useMemo(() => {
    const builtin = LANG_PICKER_OPTS_BUILTIN.map(o => [o[0], o[1], o[2], false]);
    const extras = custom
      .filter(c => !BUILTIN_LANG_CODES.has(c.code))
      .map(c => [c.code, c.flag || "🌐", c.name || c.code, true]);
    return [...builtin, ...extras];
  }, [custom]);
}

// Modal: add a custom language (BCP-47 code + display name + flag emoji).
// Styled to match AccountModal: × in top-right, stacked full-width inputs,
// section titles with underline divider, full-width primary button.
export function CustomLangModal({ theme, onClose, onAdded }) {
  const C=usePalette();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [flag, setFlag] = useState("🌐");
  const [existing, setExisting] = useState(getCustomLangs);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const refreshExisting = () => setExisting(getCustomLangs());

  const submit = () => {
    setErr(""); setOk("");
    const norm = code.toLowerCase().trim();
    if (!norm) { setErr(t("Введите код языка (например, cy)")); return; }
    if (!/^[a-z]{2,3}(-[a-z]{2,4})?$/i.test(norm)) {
      setErr(t("Код должен быть в формате BCP-47: cy, eu, nv, gd, zh-Hant…"));
      return;
    }
    if (BUILTIN_LANG_CODES.has(norm)) { setErr(t("Этот язык уже есть во встроенном списке")); return; }
    const payload = { code: norm, flag: flag.trim() || "🌐", name: name.trim() || norm.toUpperCase() };
    addCustomLang(payload);
    // Fire-and-forget Supabase push — wired in App.js so the language appears
    // on the user's other devices on next sync. Falls through to local-only
    // when offline or signed-out (queued in storage.js).
    try { window.__acSyncAddLang?.(payload); } catch {}
    refreshExisting();
    setOk(t("«{0}» добавлен.",[payload.name]));
    setCode(""); setName(""); setFlag("🌐");
    setTimeout(() => onAdded?.(norm), 250);
  };

  const [askRemove, setAskRemove] = useState(null);
  const handleRemove = (c) => {
    removeCustomLang(c);
    try { window.__acSyncRemoveLang?.(c); } catch {}
    refreshExisting();
  };

  const sectionTitle = {fontSize:15,fontWeight:600,color:C.text,margin:'4px 0 10px',paddingBottom:6,borderBottom:`1px solid ${C.border}`};
  const code_ = {background:C.surface,padding:"1px 5px",borderRadius:6};
  return (
    <Modal size="sm" title={t("Свой язык")} onClose={onClose}>
      <div style={{fontSize:13,color:C.faint,lineHeight:1.5,marginBottom:16}}>
        {t("Любой BCP-47 код:")} <code style={code_}>cy</code> {t("валлийский,")}
        {" "}<code style={code_}>eu</code> {t("баскский,")}
        {" "}<code style={code_}>nv</code> {t("навахо.")}
      </div>
      <div style={sectionTitle}>{t("Новый язык")}</div>
      <Field label={t("Код")} value={code} onChange={e=>{setCode(e.target.value);setErr("");}} maxLength={8}
        placeholder={t("например, cy")} onKeyDown={e=>e.key==="Enter"&&submit()}/>
      <Field label={t("Название")} value={name} onChange={e=>setName(e.target.value)} maxLength={40}
        placeholder={t("например, Cymraeg")} onKeyDown={e=>e.key==="Enter"&&submit()}/>
      <Field label={t("Флаг (эмодзи)")} value={flag} onChange={e=>setFlag(e.target.value)} maxLength={4}
        placeholder="🌐" onKeyDown={e=>e.key==="Enter"&&submit()} inputStyle={{textAlign:"center",fontSize:18}}/>
      {err&&<div role="alert" style={{background:C.surface,border:`1px solid ${C.danger}`,borderRadius:9,padding:"9px 13px",fontSize:13,color:C.danger,marginBottom:10}}>{err}</div>}
      {ok&&<div role="status" style={{background:C.surface,border:`1px solid ${C.green}`,borderRadius:9,padding:"9px 13px",fontSize:13,color:C.green,marginBottom:10}}>{ok}</div>}
      <Button full variant="primary" onClick={submit} style={{marginBottom:18}}>{t("Добавить язык")}</Button>
      {existing.length > 0 && (
        <>
          <div style={sectionTitle}>{t("Ваши языки")}</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {existing.map(c => (
              <div key={c.code} style={{display:"flex",alignItems:"center",gap:10,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"6px 6px 6px 12px"}}>
                <span style={{fontSize:18,lineHeight:1}}>{c.flag}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,color:C.text,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
                  <div style={{fontSize:11.5,color:C.faint,fontFamily:"monospace",marginTop:2}}>{c.code}</div>
                </div>
                <IconButton icon="close" variant="ghost" size={36} label={t("Удалить «{0}»",[c.name])} onClick={()=>setAskRemove(c.code)}/>
              </div>
            ))}
          </div>
        </>
      )}
      {askRemove&&<Confirm title={t("Удалить «{0}»?",[askRemove])} body={t("Карточки и статистика останутся.")} confirmLabel={t("Удалить")}
        onConfirm={()=>handleRemove(askRemove)} onClose={()=>setAskRemove(null)}/>}
    </Modal>
  );
}
export function LangPicker({activeLang,onChange,langCounts={},theme,compact=false,alignRight=false}){
  const C=usePalette();
  const [open,setOpen]=useState(false);
  const [showCustom,setShowCustom]=useState(false);
  const ref=useRef(null);
  const allOpts=useAllLangOpts();
  useEffect(()=>{
    if(!open)return;
    const h=(e)=>{if(!ref.current?.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[open]);
  // Языки сортируются по объёму контента (desc), при равенстве — оригинальный порядок.
  // Активный язык всегда первый, чтобы был очевиден.
  const sortedOpts=useMemo(()=>{
    const withIdx=allOpts.map((opt,i)=>({opt,count:langCounts[opt[0]]||0,idx:i}));
    withIdx.sort((a,b)=>{
      if(a.opt[0]===activeLang)return-1;
      if(b.opt[0]===activeLang)return 1;
      if(b.count!==a.count)return b.count-a.count;
      return a.idx-b.idx;
    });
    return withIdx;
  },[langCounts,activeLang,allOpts]);
  const sel=allOpts.find(o=>o[0]===activeLang);
  return(
    <div ref={ref} style={{position:'relative',flexShrink:0}}>
      <button onClick={()=>setOpen(v=>!v)} aria-haspopup="listbox" aria-expanded={open}
        title={t("Изучаемый язык: главная, повторение и статистика показывают его")}
        aria-label={t("Язык: {0}",[sel?sel[2]:t("не выбран")])}
        style={{background:activeLang?'transparent':C.blue,border:`1px solid ${activeLang?C.border:C.blue}`,
                color:activeLang?C.text:'#fff',borderRadius:8,height:40,padding:compact?'0 8px':'0 10px 0 12px',fontSize:14,fontWeight:500,
                fontFamily:SANS,cursor:'pointer',display:'flex',alignItems:'center',gap:6,flexShrink:0,maxWidth:compact?120:220}}
        className={activeLang?'ac-ibtn':'ac-press'}>
        <span style={{fontSize:16,lineHeight:1}}>{sel?sel[1]:'🌐'}</span>
        <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{sel?(compact?sel[0].toUpperCase():sel[2]):(compact?t("Язык"):t("Выбрать язык"))}</span>
        <Icon n="chevronDown" s={14}/>
      </button>
      {open&&(
        <div role="listbox" aria-label={t("Язык")} data-panel="" style={{position:'absolute',top:'calc(100% + 6px)',...(alignRight?{right:0}:{left:0}),zIndex:200,...floatSurface(C),
                     border:`1px solid ${C.border}`,borderRadius:10,boxShadow:`0 12px 32px -12px ${C.shadowMd}`,
                     padding:4,width:240,maxWidth:'calc(100vw - 24px)',maxHeight:'min(380px, 70vh)',overflowY:'auto'}}>
          {sortedOpts.map(({opt:[code,flag,name,isCustom],count})=>(
            <button key={code} onClick={()=>{onChange?.(code);setOpen(false);}} role="option" aria-selected={activeLang===code} className="ac-ibtn"
              style={{display:'flex',alignItems:'center',gap:10,width:'100%',minHeight:40,background:activeLang===code?C.bluePale:'transparent',
                      border:'none',borderRadius:8,padding:'0 10px',fontSize:14,fontFamily:SANS,
                      color:activeLang===code?C.blueDark:C.text,fontWeight:activeLang===code?600:400,
                      cursor:'pointer',textAlign:'left'}}>
              <span style={{fontSize:16}}>{flag}</span>
              <span style={{flex:1}}>{name}</span>
              {isCustom&&<span title={t("Свой язык")} style={{fontSize:9,color:C.faint,fontWeight:700,letterSpacing:'.05em'}}>USER</span>}
              {count>0&&<span style={{fontSize:11,color:C.faint,background:C.surface,
                                       borderRadius:12,padding:'1px 7px',fontWeight:600}}>{count}</span>}
              {activeLang===code&&<Icon n="check" s={15}/>}
            </button>
          ))}
          <div style={{height:1,background:C.border,margin:'6px 4px'}}/>
          <button onClick={()=>{setOpen(false);setShowCustom(true);}} className="ac-ibtn"
            style={{display:'flex',alignItems:'center',gap:10,width:'100%',minHeight:40,background:'transparent',
                    border:'none',borderRadius:8,padding:'0 10px',fontSize:14,fontFamily:SANS,
                    color:C.blueDark,fontWeight:500,cursor:'pointer',textAlign:'left'}}>
            <Icon n="plus" s={16}/>
            <span>{t("Свой язык…")}</span>
          </button>
        </div>
      )}
      {showCustom&&<CustomLangModal theme={theme} onClose={()=>setShowCustom(false)}
        onAdded={(code)=>{setShowCustom(false);onChange?.(code);}}/>}
    </div>
  );
}
